import { useState, useEffect } from "react";
import { Box, Dialog, Tab, Tabs, DialogTitle } from "@mui/material";
import { IAdvisor, IUser } from "../../../types";
import { ProfileTab } from "../users/ProfileTab";
import { HighlightsTab } from "./HighlightsTab";
import { SharesTab } from "./SharesTab";
import { LinksTab } from "./LinksTab";
import { SettingsTab } from "./SettingsTab";
import { OnboardingTab } from "./OnboardingTab";
import { useAuth0 } from "@auth0/auth0-react";
import { getAdvisors } from "../services/gutcheck-admin-api.service";

export const AdvisorModal = ({ 
    open, 
    onClose, 
    advisor: initialAdvisor, 
    onUpdate,
    onAdvisorUpdate,
    initialTab = "profile"
}: { 
    open: boolean, 
    onClose: () => void, 
    advisor: IAdvisor,
    onUpdate?: () => void,
    onAdvisorUpdate?: (updatedAdvisor: IAdvisor) => void,
    initialTab?: string
}) => {
    const { getAccessTokenSilently } = useAuth0();
    const [activeTab, setActiveTab] = useState(initialTab);
    const [advisor, setAdvisor] = useState<IAdvisor>(initialAdvisor);
    const [loading, setLoading] = useState(false);

    // Load fresh advisor data when modal opens
    useEffect(() => {
        if (open && initialAdvisor.id) {
            loadFreshAdvisor();
        }
    }, [open, initialAdvisor.id]);

    // Update local advisor when initialAdvisor changes
    useEffect(() => {
        setAdvisor(initialAdvisor);
    }, [initialAdvisor]);

    const loadFreshAdvisor = async () => {
        if (!initialAdvisor.survey_link?.project?.id) return;
        
        setLoading(true);
        try {
            const accessToken = await getAccessTokenSilently();
            const { data, error } = await getAdvisors(
                initialAdvisor.survey_link.project.id, 
                accessToken
            );
            
            if (data && data.advisors) {
                // Find the specific advisor by ID
                const freshAdvisor = data.advisors.find((a: IAdvisor) => a.id === initialAdvisor.id);
                if (freshAdvisor) {
                    setAdvisor(freshAdvisor);
                }
            } else if (error) {
                console.error('Failed to load fresh advisor data:', error);
            }
        } catch (error) {
            console.error('Error loading fresh advisor data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setActiveTab(newValue);
    };

    const handleUpdate = async () => {
        // Refresh local advisor data after any update
        await loadFreshAdvisor();
        
        // Also notify parent to refresh their data
        if (onUpdate) {
            onUpdate();
        }
    };

    const handleFieldUpdate = (updates: Partial<IAdvisor>) => {
        // Update specific fields without triggering a full reload
        const updatedAdvisor = {
            ...advisor,
            ...updates
        };
        setAdvisor(updatedAdvisor);
        
        // Notify parent to update their local advisor data
        if (onAdvisorUpdate) {
            onAdvisorUpdate(updatedAdvisor);
        }
    };

    const handleUserFieldUpdate = (updates: Partial<IUser>) => {
        // Update user fields without triggering a full reload
        const updatedAdvisor = {
            ...advisor,
            user: advisor.user ? {
                ...advisor.user,
                ...updates
            } as IUser : advisor.user
        };
        setAdvisor(updatedAdvisor);
        
        // Notify parent to update their local advisor data
        if (onAdvisorUpdate) {
            onAdvisorUpdate(updatedAdvisor);
        }
    };

    const handleClose = () => {
        // Refresh parent data when modal closes
        if (onUpdate) {
            onUpdate();
        }
        onClose();
    };

    if (loading) {
        return (
            <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
                <DialogTitle>Advisor Details</DialogTitle>
                <Box sx={{ p: 3, textAlign: 'center' }}>
                    Loading advisor data...
                </Box>
            </Dialog>
        );
    }

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <h5>Advisor Details</h5>
            <Box>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab value="profile" label="Profile" />
                    <Tab value="links" label="Links" />
                    <Tab value="onboarding" label="Onboarding" />
                    <Tab value="highlights" label="Highlights" />
                    <Tab value="shares" label="Shares" />
                    <Tab value="settings" label="Settings" />
                </Tabs>
                
                <Box sx={{ mt: 2 }}>
                    {activeTab === "profile" && <ProfileTab user={advisor.user as IUser} onUpdate={handleUpdate} onFieldUpdate={handleUserFieldUpdate} />}
                    {activeTab === "links" && <LinksTab advisor={advisor} />}
                    {activeTab === "onboarding" && <OnboardingTab advisor={advisor} onUpdate={handleUpdate} onFieldUpdate={handleFieldUpdate} />}
                    {activeTab === "highlights" && <HighlightsTab advisor={advisor} onUpdate={handleUpdate} onFieldUpdate={handleFieldUpdate} />}
                    {activeTab === "shares" && <SharesTab advisor={advisor} onUpdate={handleUpdate} onFieldUpdate={handleFieldUpdate} />}
                    {activeTab === "settings" && <SettingsTab advisor={advisor} onUpdate={handleUpdate} onFieldUpdate={handleFieldUpdate} />}
                </Box>
            </Box>
        </Dialog>
    )
}